import React from "react";
import { GoogleMap, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  Drawer,
  Divider,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import { useNavigate, useLocation } from "react-router-dom";
import getBrand from "../../brands/getBrand";
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import { useGetInventory } from "../globalFunctions";
import { useHubServiceable } from "../../hooks/useHubServiceable";

const containerStyle = {
  width: "100%",
  height: "100%",
};

// Fallback center if geolocation fails
const defaultCenter = {
  lat: 12.936023, lng:80.229313
};

function AddAddress() {
  const navigate = useNavigate();
  const location = useLocation();
  const getInventerory = useGetInventory();
  const brand = getBrand();
  const userId = useSelector((state) => state.user.id);
  const selectedHub = useSelector((state) => state.user.selected_hub);
  const inventory = useSelector((state) => state.inventory?.inventory || []);
  console.log("-------inventory--addaddress--->>>>>", inventory);
  if (inventory.length === 0) {
    getInventerory();
  }

  // Resolve current hub id and filter matching inventory (selected hub)
  const hubId = React.useMemo(() => {
    // Prefer Redux selected_hub
    if (selectedHub) {
      return Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
    }
    // Fallback to localStorage
    const raw = localStorage.getItem("selected_hub");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      const num = Number(raw);
      return Number.isNaN(num) ? raw : num;
    }
  }, [selectedHub]);

  const selectedInventory = React.useMemo(() => {
    if (!hubId || !Array.isArray(inventory)) return null;
    const hubIdStr = String(hubId);
    return (
      inventory.find(
        (inv) =>
          String(inv?.value) === hubIdStr ||
          String(inv?.id) === hubIdStr
      ) || null
    );
  }, [inventory, hubId]);

  const { serviceablePincodes, radiusConfig, boundaryConfig, isAddressServiceable } = useHubServiceable(selectedInventory);

  // Circle outlines for map (min 0.5 km for visibility)
  const radiusOnlyCirclesToDraw = React.useMemo(
    () =>
      radiusConfig.map(({ lat, lng, radiusKm }) => ({
        lat,
        lng,
        radiusKm: Math.max(0.5, radiusKm),
      })),
    [radiusConfig]
  );

  console.log("AddAddress selectedInventory (by hub id):", selectedInventory);
  const { isLoaded } = useJsApiLoader({
    id: "jmart-google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places", "geometry"],
  });

  const [map, setMap] = React.useState(null);
  const [center, setCenter] = React.useState(defaultCenter);
  const [geoError, setGeoError] = React.useState("");
  const [primaryAddress, setPrimaryAddress] = React.useState("");
  const [secondaryAddress, setSecondaryAddress] = React.useState("");
  const [stateCountry, setStateCountry] = React.useState("");
  const [pin, setPin] = React.useState("");
  const lastGeocodeRef = React.useRef(null);
  const hasInitialLocationRef = React.useRef(false);
  const autocompleteRef = React.useRef(null);

  // Address drawer state
  const [addressDrawerOpen, setAddressDrawerOpen] = React.useState(false);
  const [addressType, setAddressType] = React.useState("Home");
  const [otherAddressType, setOtherAddressType] = React.useState("");
  const [addressName, setAddressName] = React.useState("");
  const [doorFlatNo, setDoorFlatNo] = React.useState("");
  const [addressLine, setAddressLine] = React.useState("");
  const [landmark, setLandmark] = React.useState("");
  const [addressErrors, setAddressErrors] = React.useState({
    name: "",
    doorFlatNo: "",
    addressLine: "",
    otherType: "",
  });
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  const serviceableOverlaysRef = React.useRef([]);
  const boundaryOverlaysRef = React.useRef([]);

  // Draw circles on map when hub_setup is "radius" or "radius_and_pincode"
  React.useEffect(() => {
    if (!map || !window.google?.maps || radiusOnlyCirclesToDraw.length === 0) {
      serviceableOverlaysRef.current.forEach((c) => {
        if (c && c.setMap) c.setMap(null);
      });
      serviceableOverlaysRef.current = [];
      return;
    }
    serviceableOverlaysRef.current.forEach((c) => {
      if (c && c.setMap) c.setMap(null);
    });
    serviceableOverlaysRef.current = [];
    const strokeColor = "#FF0000";
    const fillColor = "#FF0000";
    radiusOnlyCirclesToDraw.forEach(({ lat, lng, radiusKm }) => {
      try {
        const circle = new window.google.maps.Circle({
          map,
          center: { lat, lng },
          radius: radiusKm * 1000, // meters
          fillColor,
          fillOpacity: 0.12,
          strokeColor,
          strokeWeight: 2.5,
          strokeOpacity: 1,
        });
        serviceableOverlaysRef.current.push(circle);
      } catch (err) {
        console.warn("AddAddress: could not draw serviceable circle", err);
      }
    });
    return () => {
      serviceableOverlaysRef.current.forEach((c) => {
        if (c && c.setMap) c.setMap(null);
      });
      serviceableOverlaysRef.current = [];
    };
  }, [map, radiusOnlyCirclesToDraw]);

  // Draw boundary polygons on map when hub_setup is "boundary"
  React.useEffect(() => {
    if (!map || !window.google?.maps || boundaryConfig.length === 0) {
      boundaryOverlaysRef.current.forEach((p) => {
        if (p && p.setMap) p.setMap(null);
      });
      boundaryOverlaysRef.current = [];
      return;
    }
    boundaryOverlaysRef.current.forEach((p) => {
      if (p && p.setMap) p.setMap(null);
    });
    boundaryOverlaysRef.current = [];
    boundaryConfig.forEach((path) => {
      try {
        const polygon = new window.google.maps.Polygon({
          map,
          paths: path,
          strokeColor: "#D32F2F",
          strokeWeight: 4,
          strokeOpacity: 1,
          fillColor: "#FF5252",
          fillOpacity: 0.08,
        });
        boundaryOverlaysRef.current.push(polygon);
      } catch (err) {
        console.warn("AddAddress: could not draw boundary polygon", err);
      }
    });
    return () => {
      boundaryOverlaysRef.current.forEach((p) => {
        if (p && p.setMap) p.setMap(null);
      });
      boundaryOverlaysRef.current = [];
    };
  }, [map, boundaryConfig]);

  const searchParams = new URLSearchParams(location.search);
  const fromCart = searchParams.get("from") === "cart";
  const specificAddressData = location.state?.specificAddressData || null;
  const editingAddressId = specificAddressData?.id || null;

  const geocodeLatLng = React.useCallback((latLng) => {
    if (!window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        const best = results[0];
        const comps = best.address_components || [];

        const findComp = (type) =>
          comps.find((c) => c.types.includes(type))?.long_name || "";

        const getPostalCode = () => {
          for (const r of results) {
            const comp = (r.address_components || []).find((c) =>
              c.types.includes("postal_code")
            );
            if (comp) return comp.long_name;
          }
          return "";
        };

        const street =
          findComp("route") ||
          findComp("sublocality") ||
          findComp("neighborhood") ||
          findComp("locality");
        const landmark = findComp("point_of_interest") || "";
        const locality = findComp("locality") || findComp("sublocality") || "";
        const city = findComp("administrative_area_level_2") || "";
        const state = findComp("administrative_area_level_1") || "";
        const country = findComp("country") || "";
        const pin = getPostalCode();

        const displayStreet = landmark || street || locality;
        const mainLine = displayStreet || best.formatted_address || "Selected location";
        const rest = [locality, city, state, pin].filter(Boolean).join(", ");

        setPrimaryAddress(mainLine);
        setSecondaryAddress(rest);
        setStateCountry([state, country].filter(Boolean).join(", "));
        setPin(pin || "");
        setSaveError("");
      } else {
        setPrimaryAddress("Selected location");
        setSecondaryAddress("");
        setStateCountry("");
        setPin("");
        setSaveError("");
      }
    });
  }, []);

  const onLoad = React.useCallback(
    function callback(mapInstance) {
      setMap(mapInstance);

      // If editing an existing address with coordinates, center map there
      if (specificAddressData) {
        const lat = parseFloat(specificAddressData.latitude);
        const lng = parseFloat(specificAddressData.longitude);
        const hasValidCoords = !Number.isNaN(lat) && !Number.isNaN(lng);

        if (hasValidCoords) {
          const current = { lat, lng };
          setCenter(current);
          mapInstance.setCenter(current);
          mapInstance.setZoom(18);
          lastGeocodeRef.current = current;
          geocodeLatLng(current);
          hasInitialLocationRef.current = true;
          return;
        }
      }

      if (navigator.geolocation) {
        // Get current location once map and API are ready
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const current = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCenter(current);
            mapInstance.setCenter(current);
            mapInstance.setZoom(18);
            lastGeocodeRef.current = current;
            geocodeLatLng(current);
            hasInitialLocationRef.current = true;
          },
          (err) => {
            const message =
              err?.code === 1
                ? "Location permission denied. Please enable location and tap \"Allow location\" below to try again."
                : err?.code === 2
                  ? "Location unavailable. Please try again."
                  : "Unable to fetch your current location. Please enable location and try again.";
            setGeoError(message);
            hasInitialLocationRef.current = true;
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        setGeoError("Geolocation is not supported by this browser.");
        hasInitialLocationRef.current = true;
      }
    },
    [geocodeLatLng, specificAddressData]
  );

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);

  const handleRequestLocation = React.useCallback(() => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by this browser.");
      return;
    }
    const mapInstance = map;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const current = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCenter(current);
        setGeoError("");
        if (mapInstance) {
          mapInstance.setCenter(current);
          mapInstance.setZoom(18);
        }
        lastGeocodeRef.current = current;
        hasInitialLocationRef.current = true;
        geocodeLatLng(current);
      },
      (err) => {
        const message =
          err?.code === 1
            ? "Location is blocked. Enable it in your browser settings (e.g. lock icon in address bar → Site settings → Location → Allow), then tap Allow location again."
            : err?.code === 2
              ? "Location unavailable. Please try again."
              : "Unable to fetch your current location. Please enable location and try again.";
        setGeoError(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [map, geocodeLatLng]);

  const handleMapIdle = React.useCallback(() => {
    if (!map || !window.google || !window.google.maps) return;
    // Wait until initial geolocation attempt is done so the first
    // address uses the user's current location instead of the fallback.
    if (!hasInitialLocationRef.current) return;
    const mapCenter = map.getCenter();
    if (!mapCenter) return;

    const current = {
      lat: mapCenter.lat(),
      lng: mapCenter.lng(),
    };
    const last = lastGeocodeRef.current;
    // Avoid spamming geocode for the same coordinates
    if (
      last &&
      Math.abs(last.lat - current.lat) < 1e-7 &&
      Math.abs(last.lng - current.lng) < 1e-7
    ) {
      return;
    }

    lastGeocodeRef.current = current;
    geocodeLatLng(current);
  }, [map, geocodeLatLng]);

  const handlePlaceChanged = React.useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete || !window.google || !window.google.maps) return;

    const place = autocomplete.getPlace();
    if (!place || !place.geometry || !place.geometry.location) {
      return;
    }

    const location = place.geometry.location;
    const current = {
      lat: location.lat(),
      lng: location.lng(),
    };

    setCenter(current);
    if (map) {
      map.panTo(current);
      map.setZoom(18);
    }
    lastGeocodeRef.current = current;
    geocodeLatLng(current);
  }, [map, geocodeLatLng]);

  const handleOpenAddressDrawer = () => {
    setSaveError("");
    // Prefill address fields
    if (specificAddressData) {
      setAddressName(specificAddressData.name || "");
      setDoorFlatNo(specificAddressData.door_flat_no || "");
      setAddressLine(specificAddressData.address || "");
      setStateCountry(specificAddressData.state_country || stateCountry);
      setPin(specificAddressData.pin || pin);
      setLandmark(specificAddressData.landmark || "");
      const type = specificAddressData.address_type || "Home";
      if (type === "Home" || type === "Office") {
        setAddressType(type);
        setOtherAddressType("");
      } else {
        setAddressType("Others");
        setOtherAddressType(type);
      }
      // Ensure center (and eventual save) use the persisted coordinates when editing
      const editLat = parseFloat(specificAddressData.latitude);
      const editLng = parseFloat(specificAddressData.longitude);
      const hasValidEditCoords = !Number.isNaN(editLat) && !Number.isNaN(editLng);
      if (hasValidEditCoords) {
        const current = { lat: editLat, lng: editLng };
        setCenter(current);
        lastGeocodeRef.current = current;
      }
    } else {
      const combinedAddress = [primaryAddress, secondaryAddress]
        .filter(Boolean)
        .join(", ");
      setAddressLine(combinedAddress);
    }
    setAddressErrors({
      name: "",
      doorFlatNo: "",
      addressLine: "",
      otherType: "",
    });
    setAddressDrawerOpen(true);
  };

  const handleSaveAddress = () => {
    const authToken = localStorage.getItem("authtoken");
    if (!authToken) {
      setSaveError("Session expired. Please sign in again.");
      return;
    }

    let accountId = userId;

    if (!accountId) {
      console.log("User not found. Please sign in again.");
      setSaveError("User not found. Please sign in again.");
      return;
    }

    const effectiveUserId = userId || localStorage.getItem("id");
    if (!effectiveUserId) {
      console.log("User not found. 11Please sign in again.");
      setSaveError("User not found. Please sign in again.");
      return;
    }

    const nextErrors = { name: "", doorFlatNo: "", addressLine: "", otherType: "" };
    if (!addressName.trim()) {
      nextErrors.name = "Name is required";
    }
    if (!doorFlatNo.trim()) {
      nextErrors.doorFlatNo = "Door / flat number is required";
    }
    if (!addressLine.trim()) {
      nextErrors.addressLine = "Address is required";
    }
    if (addressType === "Others" && !otherAddressType.trim()) {
      nextErrors.otherType = "Address type is required";
    }

    if (nextErrors.name || nextErrors.doorFlatNo || nextErrors.addressLine || nextErrors.otherType) {
      setAddressErrors(nextErrors);
      return;
    }

    // Always use the last geocoded coordinates (from drag / autocomplete / initial load)
    const coordSource = lastGeocodeRef.current || center;
    const addressLat = coordSource?.lat;
    const addressLng = coordSource?.lng;
    const selectedPin = String(pin || "").trim();

    // Hub serviceable-area check (pincode / radius / radius_and_pincode / boundary)
    const addressForCheck = { latitude: addressLat, longitude: addressLng, pin: selectedPin };
    if (!isAddressServiceable(addressForCheck)) {
      setSaveError(
        "Delivery is not available for this location. Please choose an address within this hub's service area."
      );
      return;
    }

    const payload = {
      name: addressName.trim(),
      address_type: addressType === "Others" ? otherAddressType.trim() : addressType,
      door_flat_no: doorFlatNo.trim(),
      address: addressLine.trim(),
      state_country: stateCountry,
      pin,
      landmark: landmark.trim(),
      latitude: coordSource?.lat,
      longitude: coordSource?.lng,
      account: accountId,
      created_by: Number(effectiveUserId),
      updated_by: Number(effectiveUserId),
    };

    setSaving(true);
    setSaveError("");

    const endpoint = editingAddressId
      ? `/addressupdate/${editingAddressId}`
      : `/address/`;
    const method = editingAddressId ? CustomAxios.put : CustomAxios.post;

    method(endpoint, payload)
      .then((response) => {
        if (response?.status === 200 || response?.status === 201) {
          const created = response?.data;
          if (fromCart && created?.id) {
            // Remember which address to preselect when returning to cart
            localStorage.setItem("cart_selected_address_id", String(created.id));
          }
          setAddressDrawerOpen(false);
          if (fromCart) {
            navigate("/cart");
          } else {
            navigate("/saved-addresses");
          }
        } else {
          setSaveError("Failed to save address. Please try again.");
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Error saving address:", err);
        setSaveError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Failed to save address. Please try again."
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: brand?.backgroundColor || "#F9FAFB",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: { xs: 2, md: 3 },
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          height: { xs: "56px", md: "72px" },
          gap: 1,
        }}
      >
        <Button
          startIcon={
            <ArrowBackIosIcon
              sx={{
                fontSize: "14px",
                color: brand?.theme?.textPrimary || "#111827",
              }}
            />
          }
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", minWidth: "auto", pr: 1 }}
        />
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
          }}
        >
          Select Your Location
        </Typography>
      </Box>

      {/* Search + Map section */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: { xs: "320px", md: "480px" },
          background: "#E5E7EB",
        }}
      >
        {/* Search bar overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 0,
            right: 0,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 560,
              px: 2,
              mx: "auto",
            }}
          >
            <Autocomplete
              onLoad={(instance) => {
                autocompleteRef.current = instance;
              }}
              onPlaceChanged={handlePlaceChanged}
            >
              <TextField
                fullWidth
                placeholder="Search for apartment, street name..."
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  background: "#fff",
                  borderRadius: "999px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "999px",
                  },
                }}
              />
            </Autocomplete>
          </Box>
        </Box>

        {/* Map */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
          }}
        >
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={16}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onIdle={handleMapIdle}
            options={{
              disableDefaultUI: true,
            }}
          >
            {/* Map children (markers, etc.) can go here */}
          </GoogleMap>
        </Box>

        {/* Center pin */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          
          {/* Pin icon */}
          <RoomIcon
            sx={{
              fontSize: 40,
              color: brand?.themeColor || brand?.theme?.primary || "#EC4899",
              filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.25))",
            }}
          />
        </Box>
      </Box>

      {/* Bottom sheet */}
      <Box
        sx={{
          background: "#fff",
          px: 2,
          pt: 2,
          pb: 3,
          boxShadow: "0 -4px 16px rgba(15,23,42,0.12)",
        }}
      >
        {geoError ? (
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                color: "#EF4444",
                mb: 1,
              }}
            >
              {geoError}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRequestLocation}
              sx={{
                mt: 1,
                borderRadius: "999px",
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                borderColor: brand?.themeColor || brand?.theme?.primary || "#EC4899",
                color: brand?.themeColor || brand?.theme?.primary || "#EC4899",
                "&:hover": {
                  borderColor: brand?.themeColor || brand?.theme?.primary || "#EC4899",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? "rgba(236, 72, 153, 0.08)"
                      : "rgba(236, 72, 153, 0.16)",
                },
              }}
            >
              Allow location
            </Button>
          </Box>
        ) : (
            <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RoomIcon
            sx={{
              fontSize: 20,
              color: brand?.themeColor || brand?.theme?.primary || "#EC4899",
              filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.25))",
            }}
          />
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {primaryAddress}
                </Typography>
                </Box>
            <Typography
              sx={{
                fontSize: 13,
                color: "#6B7280",
                mt: 0.5,
              }}
            >
              {secondaryAddress}
            </Typography>
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          sx={{
            mt: 2.5,
            borderRadius: "999px",
            py: 1.4,
            fontWeight: 700,
            textTransform: "none",
            fontSize: 15,
            backgroundColor:
              brand?.themeColor || brand?.theme?.primary || "#EC4899",
            
          }}
          onClick={handleOpenAddressDrawer}
        >
          Confirm Location
        </Button>
      </Box>

      {/* Address details drawer */}
      <Drawer
        anchor="bottom"
        open={addressDrawerOpen}
        onClose={() => setAddressDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 3,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 999,
              backgroundColor: "#E5E7EB",
              mx: "auto",
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              mb: 2,
              color: brand?.theme?.textPrimary || "#111827",
            }}
          >
            Save Address
          </Typography>

          {/* Address type */}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              mb: 1,
              color: "#4B5563",
            }}
          >
            Address Type
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            {["Home", "Office", "Others"].map((type) => (
              <Button
                key={type}
                variant={addressType === type ? "contained" : "outlined"}
                size="small"
                onClick={() => setAddressType(type)}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2,
                  py: 0.5,
                  fontSize: 12,
                  backgroundColor:
                    addressType === type
                      ? brand?.themeColor || brand?.theme?.primary || "#EC4899"
                      : "#FFFFFF",
                  color:
                    addressType === type
                      ? "#FFFFFF"
                      : brand?.theme?.textPrimary || "#111827",
                  borderColor:
                    addressType === type
                      ? brand?.themeColor || brand?.theme?.primary || "#EC4899"
                      : "#E5E7EB",
                }}
              >
                {type}
              </Button>
            ))}
          </Box>
          {addressType === "Others" && (
            <TextField
              label="Address type"
              value={otherAddressType}
              onChange={(e) => setOtherAddressType(e.target.value)}
              fullWidth
              size="small"
              margin="dense"
              error={!!addressErrors.otherType}
              helperText={addressErrors.otherType}
            />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Address form */}
          <TextField
            label="Full name"
            value={addressName}
            onChange={(e) => setAddressName(e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            error={!!addressErrors.name}
            helperText={addressErrors.name}
          />
          <TextField
            label="Flat / Door number"
            value={doorFlatNo}
            onChange={(e) => setDoorFlatNo(e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            error={!!addressErrors.doorFlatNo}
            helperText={addressErrors.doorFlatNo}
          />
          <TextField
            label="Street, area, city"
            value={addressLine}
            onChange={(e) => setAddressLine(e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            error={!!addressErrors.addressLine}
            helperText={addressErrors.addressLine}
          />
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              label="State, Country"
              value={stateCountry}
              fullWidth
              size="small"
              margin="dense"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="PIN code"
              value={pin}
              fullWidth
              size="small"
              margin="dense"
              InputProps={{ readOnly: true }}
            />
          </Box>
          <TextField
            label="Landmark (optional)"
            value={landmark}
            onChange={(e) => setLandmark(e.target.value)}
            fullWidth
            size="small"
            margin="dense"
          />

          {saveError && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 12,
                color: "#EF4444",
              }}
            >
              {saveError}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              borderRadius: "999px",
              py: 1.2,
              textTransform: "none",
              fontWeight: 700,
              backgroundColor:
                brand?.themeColor || brand?.theme?.primary || "#EC4899",
              "&:hover": {
                backgroundColor:
                  brand?.theme?.primary || brand?.themeColor || "#DB2777",
              },
            }}
            onClick={handleSaveAddress}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Address"}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}

export default React.memo(AddAddress);